import { Box, Button, Flex, FlexProps, Icon, Image, Stack, Text, useDisclosure } from "@chakra-ui/react";
import { useMemo } from "react";
import { IoLocationSharp } from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import { BLUE_COLOR } from "../const";
import { ParkFormModal } from "../forms/ParkFormModal";
import { useGetParkDetails } from "../hooks/useGetParkDetails";
import { LocationData } from "../types";
import { formatNumberWithPounds } from "../utils";
import { ColouredTickList } from "./ColouredTickList";
import { ImageCarousel } from "./ImageCarousel";

interface ParkOverviewProps extends FlexProps {
  parkLocationData: LocationData;
  doesAnyParkMeetRequirements: boolean;
}

export const ParkOverview: React.FC<ParkOverviewProps> = ({
  parkLocationData,
  doesAnyParkMeetRequirements,
  ...rest
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  const parkName = Object.keys(parkLocationData)?.[0];
  const numberOfResultsMeetingCriteria = Object.values(parkLocationData)?.[0].length;

  const { data: parkDetails } = useGetParkDetails(parkName);

  const cheapestCaravan = Math.min(...Object.values(parkLocationData)[0]?.map((park) => park.price));

  const onParkOverviewClick = () => navigate(parkName.toLowerCase());

  const parksText = useMemo(() => {
    if (doesAnyParkMeetRequirements) {
      if (numberOfResultsMeetingCriteria === 1) return "holiday home matches your search";
      return "holiday homes match your search";
    }
    if (numberOfResultsMeetingCriteria === 1) return "holiday home available";
    return "holiday homes available";
  }, [doesAnyParkMeetRequirements, numberOfResultsMeetingCriteria]);

  // Do not put an alert in here, it's too low level
  if (parkDetails == null) {
    return null;
  }

  return (
    <>
      <ParkFormModal isOpen={isOpen} onClose={onClose} parkId={parkDetails?.parkId} />

      <Flex flexDir="column" flex={1} maxW="316px" boxSize="100%" borderWidth={1} pt={0} {...rest}>
        <Box h="304px" position="relative" borderBottomWidth={1}>
          <Flex position="relative" overflow="hidden">
            <ImageCarousel maxW="314px" boxSize="100%" onClick={onParkOverviewClick} cursor="pointer">
              {parkDetails?.imgs.map((url, i) => (
                <Box key={i} w="100%" h="19rem">
                  <Image src={url} h="100%" w="100%" objectFit="cover" />
                </Box>
              ))}
            </ImageCarousel>
          </Flex>

          <Flex
            alignItems="center"
            fontWeight="bold"
            position="absolute"
            bg="rgba(255, 255, 255, 0.9)"
            bottom={-1}
            w="100%"
            p={2}
          >
            <Icon as={IoLocationSharp} color={BLUE_COLOR} boxSize="20px" />
            <Text>{parkDetails?.sAdd}</Text>
          </Flex>
        </Box>
        <Stack p={2} pb={4}>
          <Link to={parkName.toLowerCase()}>
            <Text fontWeight="semibold" fontSize="24px">
              {parkDetails.name}
            </Text>
          </Link>
          <Text fontSize="18px" mb={2}>
            <Text as="span" fontWeight="bold" color={BLUE_COLOR}>
              {numberOfResultsMeetingCriteria}
            </Text>{" "}
            {parksText}
          </Text>
          <ColouredTickList data={parkDetails.features.slice(0, 3)} columns={1} gap={2} />
        </Stack>
        <Stack w="100%" borderTopWidth={1} p={2} mt="auto" gap={4}>
          <Button h="100%" onClick={onOpen} p={2} borderRadius="20px">
            <Text>Request a Free Park Pack</Text>
          </Button>

          <Button variant="blue" h="100%" p={2} borderRadius="20px">
            <Link to={parkName.toLowerCase()} style={{ width: "100%" }}>
              Prices from {formatNumberWithPounds(cheapestCaravan)}
            </Link>
          </Button>
        </Stack>
      </Flex>
    </>
  );
};
